/**
 * @param {string} label - Display label for the accordion header
 * @param {Object} value - Current language values { en: "...", fr: "...", etc }
 * @param {Function} onChangeLabel - Callback when user types (languageCode, newValue)
 * @param {Function} onChange - Callback when translation completes (result)
 * @param {boolean} disabled - Disable all inputs
 * @param {string} path - Unique identifier for this translation request
 * @param {boolean} error - Show error state on inputs
 * @param {Object} InputProps - Additional props for MDInput component
 * @param {Object} actions - Object containing { translateV2 } from useTranslateActions hook
 * @param {Array} languages - Array of language objects with { code, name, logoURL }
 */
import lod_ from "lodash";
import { ExpandMore, Translate } from "@mui/icons-material";
import {
	Grid,
	Accordion,
	AccordionDetails,
	AccordionSummary,
	IconButton,
	CircularProgress,
	Tooltip,
	Chip,
	Menu,
	MenuItem
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDInput from "components/Basics/MDInput";
import MDTypography from "components/Basics/MDTypography";
import Joi from "joi";
import { useEffect, useState, useRef, useCallback } from "react";
import { t } from "i18next";

export const inputLabelLanguageSchemaValue = Joi.object().pattern(
	Joi.string().trim().required().description("Code langue").empty(),
	Joi.string().trim().required().description("Texte traduit").min(1).empty()
);
export const inputLabelLanguageSchemaValueString = `Joi.object().pattern(
	Joi.string().trim().required().description("Code langue").empty(),
	Joi.string().trim().required().description("Texte traduit").min(1).empty()
)`;

const InputLabelLanguage = ({
	label,
	value,
	onChangeLabel,
	onChange,
	disabled,
	path,
	error,
	InputProps = {},
	actions,
	languages,
	...rest
}) => {
	const [validCount, setValidCount] = useState(0);
	const [translating, setTranslating] = useState(false);
	const [translateSuccess, setTranslateSuccess] = useState(false);
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [notEmptyLanguages, setNotEmptyLanguages] = useState([]);
	const [localValues, setLocalValues] = useState(value ?? {});
	const debounceTimerRef = useRef({});

	const { translateV2 } = actions || {};
	const languageCodes = languages.map(lang => lang.code);

	/**
	 * Sync local values when parent value changes
	 */
	useEffect(() => {
		setLocalValues(value);
	}, [value]);

	/**
	 * Debounced onChange handler to prevent laggy input
	 */
	const debouncedOnChangeLabel = useCallback(
		(languageCode, newValue) => {
			// Update local state immediately for instant UI feedback
			setLocalValues(prev => ({
				...prev,
				[languageCode]: newValue
			}));

			// Clear existing timer for this language if any
			if (debounceTimerRef.current[languageCode]) {
				clearTimeout(debounceTimerRef.current[languageCode]);
			}

			// Set new timer for parent callback
			debounceTimerRef.current[languageCode] = setTimeout(() => {
				onChangeLabel(languageCode, newValue);
			}, 300); // 300ms debounce delay
		},
		[onChangeLabel]
	);

	/**
	 * Handle menu open for language selection
	 */
	const handleMenuOpen = e => {
		e.stopPropagation();
		e.preventDefault();
		if (notEmptyLanguages.length > 0) {
			setMenuAnchorEl(e.currentTarget);
		}
	};

	/**
	 * Handle menu close
	 */
	const handleMenuClose = e => {
		e?.stopPropagation();
		e?.preventDefault();
		setMenuAnchorEl(null);
	};

	/**
	 * Handle language selection and start translation
	 */
	const handleLanguageSelect = (e, selectedLanguageCode) => {
		handleMenuClose(e);

		if (!translateV2 || translating) return;

		const selectedLanguage = languages.find(lang => lang.code === selectedLanguageCode);
		if (!selectedLanguage) return;

		setTranslating(true);
		setTranslateSuccess(false);

		const sourceText = value[selectedLanguageCode] || "";
		const sourceLanguageData = { [selectedLanguageCode]: sourceText };

		translateV2(
			path,
			selectedLanguageCode,
			sourceLanguageData,
			inputLabelLanguageSchemaValue,
			inputLabelLanguageSchemaValueString,
			({ code, result, success }) => {
				if (success) {
					onChange(result);
					setTranslateSuccess(true);
					// Reset success indicator after 2 seconds
					setTimeout(() => {
						setTranslateSuccess(false);
					}, 2000);
				}
				setTranslating(false);
			}
		);
	};

	/**
	 * Count valid values
	 */
	const getEmptyValueCount = () => {
		let notEmptyLanguagesCodes = [];
		Object.entries(value ?? {}).forEach(([key, value]) => {
			if (!languageCodes.includes(key)) return;

			const { error: validationError } = inputLabelLanguageSchemaValue.validate({ [key]: value });
			if (lod_.isNil(validationError)) {
				notEmptyLanguagesCodes.push(key);
			}
		});
		const notEmptyLangs = languages.filter(lang => notEmptyLanguagesCodes.includes(lang.code));
		setNotEmptyLanguages(notEmptyLangs);
		setValidCount(notEmptyLanguagesCodes.length);
		return notEmptyLanguagesCodes;
	};

	useEffect(() => {
		getEmptyValueCount();
	}, [value, languages]);

	/**
	 * Cleanup debounce timers on unmount
	 */
	useEffect(() => {
		return () => {
			Object.values(debounceTimerRef.current).forEach(timer => clearTimeout(timer));
		};
	}, []);

	return (
		<Grid
			size="grow"
			sx={{
				flexBasis: "100%",
				minWidth: "100%"
			}}
		>
			<Accordion className="customAccordion" style={{ boxShadow: "none" }}>
				<AccordionSummary
					expandIcon={<ExpandMore />}
					aria-controls="panel1a-content"
					id="panel1a-header"
				>
					<MDBox
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						sx={{ width: "100%" }}
					>
						<MDTypography variant="caption" fontSize="0.875rem">
							{label}
						</MDTypography>
						<MDBox display="flex" alignItems="center" gap={1}>
							<Chip
								label={`${validCount}/${languages.length}`}
								size="small"
								variant="outlined"
								color={validCount === languages.length ? "success" : "default"}
								sx={{
									fontWeight: 600,
									backgroundColor:
										validCount === languages.length ? "success.lighter" : "transparent"
								}}
							/>
							<Tooltip
								title={
									translating
										? t("ACTIONS.TRANSLATION.translation")
										: t("ACTIONS.TRANSLATION.selectLanguageFrom")
								}
								arrow
								placement="top"
							>
								<span>
									<IconButton
										size="small"
										onClick={handleMenuOpen}
										disabled={translating || !translateV2 || notEmptyLanguages.length === 0}
										sx={{
											transition: "all 0.3s ease",
											color: translateSuccess ? "success.main" : "inherit",
											"&:hover": {
												backgroundColor: "action.hover"
											}
										}}
									>
										{translating ? (
											<CircularProgress size={24} color="info" />
										) : (
											<Translate sx={{ fontSize: 20 }} />
										)}
									</IconButton>
								</span>
							</Tooltip>
							<Menu
								anchorEl={menuAnchorEl}
								open={Boolean(menuAnchorEl)}
								onClose={handleMenuClose}
								placement="left-start"
							>
								{notEmptyLanguages.map(lang => (
									<MenuItem key={lang.code} onClick={e => handleLanguageSelect(e, lang.code)}>
										{lang.logoURL && (
											<img
												src={lang.logoURL}
												alt={lang.code}
												style={{
													width: "20px",
													height: "20px",
													borderRadius: "50%",
													marginRight: "10px"
												}}
											/>
										)}
										<MDTypography variant="body2">
											{lang.name} ({lang.code})
										</MDTypography>
									</MenuItem>
								))}
							</Menu>
						</MDBox>
					</MDBox>
				</AccordionSummary>
				<AccordionDetails>
					{languages.map((LANGUAGE, index) => {
						if (!lod_.isNil(LANGUAGE?.code) && !lod_.isEmpty(LANGUAGE?.code)) {
							return (
								<MDBox
									sx={{ mb: 1 }}
									display="flex"
									bgColor="white"
									p={1}
									borderRadius="md"
									style={{
										border: "1px solid rgb(232, 232, 238)"
									}}
									flexDirection="column"
									key={index}
								>
									{/* Top bar */}
									<MDBox display="flex" alignItems="center" justifyContent="space-between">
										<MDBox display="flex" alignItems="center">
											{/* Logo */}
											{LANGUAGE.logoURL && (
												<img
													src={LANGUAGE.logoURL}
													alt={LANGUAGE.code}
													style={{
														width: "20px",
														height: "20px",
														borderRadius: "50%",
														marginRight: "10px"
													}}
												/>
											)}
											{/* Name */}
											<MDTypography variant="body2">{LANGUAGE.name}</MDTypography>
										</MDBox>
									</MDBox>
									{/* Editor, depending on answer type */}
									<MDBox sx={{ mt: 1 }}>
										<MDInput
											disabled={disabled}
											error={error}
											type="text"
											className="dialogInput"
											value={(localValues ?? {})[LANGUAGE.code] || ""}
											onChange={e => debouncedOnChangeLabel(LANGUAGE.code, e.target.value)}
										/>
									</MDBox>
								</MDBox>
							);
						} else {
							return <MDBox></MDBox>;
						}
					})}
				</AccordionDetails>
			</Accordion>
		</Grid>
	);
};
export default InputLabelLanguage;
